<template>
  <div>
    <div class="field is-grouped">
      <div class="control">
        <input :value="liveCondition.value" type="number" class="input" @input="secondsChanged" />
        <p class="help is-danger" v-if="isRequiredInvalid">Number of seconds required</p>
        <p class="help is-danger" v-else-if="isNumericInvalid">Must be a valid whole number</p>
        <p class="help is-danger" v-else-if="isMinValueInvalid">Must be greater than 0</p>
      </div>
      <div class="control">
        <div class="relative-text">
          <span>seconds</span>
        </div>
      </div>
    </div>

    <div class="control">
      <n-button variant="primary" @click="complete()">Done</n-button>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ConditionViewModel } from '../models/conditionViewModel';
import { ConditionOperationType } from '../models/conditions';
import { required, numeric, requiredIf } from '@vuelidate/validators';
import useVuelidate from '@vuelidate/core';
import { PropType, ref, onMounted, computed } from 'vue';
import VuelidateUtil from '@shared/util/vuelidateUtil';

const props = defineProps({
  condition: {
    type: Object as PropType<ConditionViewModel>,
    required: true,
  },
});

const emit = defineEmits<{
  (e: 'changed', condition: ConditionViewModel): void;
  (e: 'completed', condition: ConditionViewModel): void;
}>();

const liveCondition = ref<ConditionViewModel>({
  attribute: '',
  operation: '',
  type: '',
  value: '',
  result: true,
});

const greaterThanZero = (value: number) => value > 0;
const validations = computed(() => ({
  liveCondition: {
    value: {
      required: requiredIf(() => liveCondition.value.operation !== ConditionOperationType.IsSet),
      minValue: greaterThanZero,
      numeric,
    },
  },
}));

const $v = useVuelidate(validations, { liveCondition });
const isRequiredInvalid = computed(() => VuelidateUtil.isRequiredInvalid($v, (v) => v.liveCondition.value));
const isNumericInvalid = computed(() => VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'numeric'));
const isMinValueInvalid = computed(() =>
  VuelidateUtil.isValidationInvalid($v, (v) => v.liveCondition.value, 'minValue'),
);

onMounted(() => {
  if (props.condition) {
    liveCondition.value = {
      attribute: props.condition.attribute,
      operation: ConditionOperationType.Equals,
      type: props.condition.type,
      value: props.condition.value,
      result: props.condition.result,
      unit: 'seconds',
      invalid: props.condition.invalid,
    };

    if (liveCondition.value.invalid) {
      $v.value.$touch();
    }
  }
});

function secondsChanged(event: any) {
  liveCondition.value.value = event.target.value;
  $v.value.$touch();
  emitConditionChanged();
}

function emitConditionChanged() {
  liveCondition.value.invalid = $v.value.$invalid;
  emit('changed', liveCondition.value);
}

function complete() {
  liveCondition.value.invalid = $v.value.$invalid;
  emit('completed', liveCondition.value);
}
</script>

<style lang="scss" scoped>
.field.is-grouped > .control {
  flex-shrink: 1;
}
.relative-text {
  margin-right: 0.75rem;
  display: flex;
  align-items: center;
  height: 2.25em;
}
</style>
